import { CheckCircle, Download, ArrowRight, ShoppingCart, MapPin } from "lucide-react";
import { SERVICE_DEFS, COMPARISON_KEYS, COMPARISON_DATA } from "./constants";
import { CellIcon } from "./CellIcon";

/** Revenue calculator + services bento + comparison table + plugins + results + reviews.
 *  Purely marketing content — takes only `t`, no handlers required.
 *  Pricing section is separate because it needs the `handleCheckout` callback. */
export function LandingMarketingSections({ t }) {
  return (
    <>
      {/* SERVICES BENTO */}
      <section className="py-24 px-6" id="services" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-[#a855f7] font-['JetBrains_Mono'] text-sm tracking-[0.3em] uppercase mb-4">{t.svc_tag || "18 tools. one platform."}</p>
          <h2 className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-12">
            {t.svc_h1 || "EVERYTHING YOU NEED."}<br /><span className="text-[var(--color-text-muted)]">{t.svc_h1_2 || "NOTHING YOU DON'T."}</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {SERVICE_DEFS.map((s, i) => (
              <a key={i} href={s.link || "#"} className={`${s.span} bg-[var(--color-surface)] border border-[var(--color-border)] p-6 hover:border-[var(--color-primary)] transition-all group relative block`} data-testid={`service-${i}`}>
                {s.hot && <span className="absolute top-3 right-3 px-2 py-0.5 bg-[#a855f7] text-[10px] font-bold uppercase tracking-wider">NEW</span>}
                <s.icon className="w-6 h-6 text-[var(--color-text-muted)] group-hover:text-[var(--color-text)] transition-colors mb-4" />
                <h3 className="font-bold text-white mb-1 font-['Cabinet_Grotesk']">{t[`${s.nk}_name`] || s.nk}</h3>
                <p className="text-sm text-[var(--color-text-muted)]">{t[`${s.nk}_desc`] || ""}</p>
                {t[`${s.nk}_why`] && <p className="text-[10px] text-[var(--color-text-muted)] mt-2 opacity-0 group-hover:opacity-100 transition-opacity font-['JetBrains_Mono']">{t[`${s.nk}_why`]}</p>}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-24 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]" data-testid="comparison-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-[#FFE600] font-['JetBrains_Mono'] text-sm tracking-[0.3em] uppercase mb-4">{t.cmp_tag || "The competition can't compete"}</p>
          <h2 className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-12">
            {t.cmp_h1 || "WHY"} <span className="text-[#a855f7]">{t.cmp_h1_highlight || "TOPRANKPULSE"}</span><br />{t.cmp_h1_2 || "IS THE ONLY CHOICE"}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left px-4 py-4 text-[var(--color-text-muted)] font-['JetBrains_Mono'] text-xs uppercase tracking-wider">{t.cmp_feature || "Feature"}</th>
                  <th className="px-4 py-4 text-center bg-[var(--color-surface-alt)] border-x border-[var(--color-border)]">
                    <span className="text-[#a855f7] font-black font-['Cabinet_Grotesk'] text-lg">TopRankPulse</span><br />
                    <span className="text-[#FFE600] font-['JetBrains_Mono'] text-xs">{t.cmp_price || "€49/mo"}</span>
                  </th>
                  <th className="px-4 py-4 text-center text-[var(--color-text-muted)] font-['JetBrains_Mono'] text-xs">Ahrefs<br /><span className="text-[var(--color-text-muted)]">€99/mo</span></th>
                  <th className="px-4 py-4 text-center text-[var(--color-text-muted)] font-['JetBrains_Mono'] text-xs">Semrush<br /><span className="text-[var(--color-text-muted)]">€130/mo</span></th>
                  <th className="px-4 py-4 text-center text-[var(--color-text-muted)] font-['JetBrains_Mono'] text-xs">Yoast<br /><span className="text-[var(--color-text-muted)]">€99/yr</span></th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_DATA.map((row, i) => (
                  <tr key={i} className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface)]/50 transition-colors">
                    <td className="px-4 py-3 text-sm text-[var(--color-text)]">{t[COMPARISON_KEYS[i]] || COMPARISON_KEYS[i]}</td>
                    <td className="px-4 py-3 text-center bg-[var(--color-surface-alt)] border-x border-[var(--color-border)]"><CellIcon val={row.us} /></td>
                    <td className="px-4 py-3 text-center"><CellIcon val={row.ahrefs} /></td>
                    <td className="px-4 py-3 text-center"><CellIcon val={row.semrush} /></td>
                    <td className="px-4 py-3 text-center"><CellIcon val={row.yoast} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}

export function PluginsSection({ t }) {
  const plugins = [
    {
      id: "wordpress",
      name: "WordPress",
      href: "/plugin",
      desc: t.wp_desc || "Add real-time SEO/GEO scanning to any WordPress site. Auto-embed scan widget.",
      accent: "#21759B",
      cta: t.download_free || "Download free",
      badge: "FREE",
    },
    {
      id: "woocommerce",
      name: "WooCommerce",
      href: "/plugin#woocommerce",
      desc: t.woo_desc || "E-commerce add-on: scan product pages for AI visibility + fix missing meta.",
      accent: "#7F54B3",
      cta: t.download_free || "Download free",
      badge: "FREE",
    },
    {
      id: "shopify",
      name: "Shopify",
      href: "/plugin#shopify",
      desc: t.shopify_home_desc || "Embed the scan on any Shopify store with a single script tag.",
      accent: "#96BF48",
      cta: t.embed_script || "Embed script",
      badge: "FREE",
    },
    {
      id: "magento",
      name: "Magento",
      href: "/plugin#magento",
      desc: t.mag_desc || "Adobe Commerce / Magento 2 extension for scan + structured-data fixes.",
      accent: "#ef4033",
      cta: t.install_ext || "Install extension",
      badge: "BETA",
    },
    {
      id: "wix",
      name: "Wix",
      href: "/plugin#wix",
      desc: t.wix_desc || "One-click embed for Wix sites — adds a scan button to your header.",
      accent: "#0c6efc",
      cta: t.add_to_site || "Add to site",
      badge: "FREE",
    },
    {
      id: "webflow",
      name: "Webflow",
      href: "/plugin#webflow",
      desc: t.wf_desc || "Copy-paste a <script> embed and start tracking AI visibility on your Webflow site.",
      accent: "#4353ff",
      cta: t.copy_snippet || "Copy snippet",
      badge: "FREE",
    },
    {
      id: "chrome",
      name: "Chrome Extension",
      href: "/plugin#chrome-extension",
      desc: t.ce_desc || "Scan any open page in 1 click. Compare competitors instantly from your browser.",
      accent: "#4285F4",
      cta: t.install_chrome || "Add to Chrome",
      badge: "FREE",
    },
    {
      id: "api",
      name: "REST API",
      href: "/plugin#api",
      desc: t.api_desc || "Programmatic access to scans, AI visibility, and monitoring for your tooling.",
      accent: "#f59e0b",
      cta: t.view_docs || "View docs",
      badge: "PRO",
    },
    {
      id: "zapier",
      name: "Zapier",
      href: "/plugin#zapier",
      desc: t.zap_desc || "Trigger workflows on score drops, brand mentions, competitor moves.",
      accent: "#FF4A00",
      cta: t.connect || "Connect",
      badge: "PRO",
    },
    {
      id: "local_seo",
      name: t.local_seo_label || "Local SEO pages",
      href: "/seo/en",
      desc: t.cities_desc || "500+ city-level landing pages — rank locally for 'SEO in {your city}'.",
      accent: "#FFE600",
      cta: t.browse_pages || "Browse pages",
      badge: "FREE",
    },
  ];
  return (
    <section className="py-24 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)]" data-testid="plugins-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-[#a855f7] font-['JetBrains_Mono'] text-sm tracking-[0.3em] uppercase mb-4">{t.free_plugins_label || "Plugins & Integrations"}</p>
          <h2 className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4">
            {t.works_with || "WORKS WITH"}<br /><span className="text-[#a855f7]">{t.platform_label || "EVERYTHING YOU ALREADY USE"}</span>
          </h2>
          <p className="text-[var(--color-text-muted)] max-w-2xl mx-auto">{t.plugin_install_desc || "No migration needed. Drop our scanner into your existing stack in minutes."}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {plugins.map((p) => (
            <a
              key={p.id}
              href={p.href}
              data-testid={`plugin-${p.id}`}
              className="group block p-5 bg-[var(--color-surface)] border border-[var(--color-border)] transition-all hover:-translate-y-0.5"
              style={{ ["--accent"]: p.accent }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = p.accent)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
            >
              <div className="flex items-start justify-between mb-3">
                <div
                  className="w-11 h-11 flex items-center justify-center font-black text-lg"
                  style={{ background: `${p.accent}20`, color: p.accent }}
                >
                  {p.name.charAt(0)}
                </div>
                <span
                  className="text-[9px] font-black px-1.5 py-0.5 tracking-wider"
                  style={{
                    background: p.badge === "FREE" ? "rgba(16,185,129,0.12)" : p.badge === "PRO" ? "rgba(168,85,247,0.12)" : "rgba(251,191,36,0.12)",
                    color: p.badge === "FREE" ? "#10b981" : p.badge === "PRO" ? "#c084fc" : "#fbbf24",
                  }}
                >
                  {p.badge}
                </span>
              </div>
              <h3 className="text-white font-bold font-['Cabinet_Grotesk'] text-base mb-1.5">{p.name}</h3>
              <p className="text-[var(--color-text-muted)] text-xs leading-relaxed mb-3 min-h-[48px]">{p.desc}</p>
              <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: p.accent }}>
                {p.cta} <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ResultsStatsSection({ t }) {
  return (
    <section className="py-20 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)]" data-testid="results-section">
      <div className="max-w-7xl mx-auto">
        <p className="text-[#FFE600] font-['JetBrains_Mono'] text-sm tracking-[0.3em] uppercase mb-4 text-center">{t.sp_tag || "REAL RESULTS FROM REAL BUSINESSES"}</p>
        <h2 className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-16 text-center">
          {t.sp_title || "Numbers That"} <span className="text-[#a855f7]">{t.sp_highlight || "Speak"}</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: "721%", label: t.sp_stat1 || "Avg. Traffic Growth", sub: t.sp_stat1_sub || "within 90 days", color: "text-emerald-400" },
            { value: "3,800", label: t.sp_stat2 || "Impressions/Day", sub: t.sp_stat2_sub || "hyper-niche industry", color: "text-[#FFE600]" },
            { value: "47%", label: t.sp_stat3 || "CTR Increase", sub: t.sp_stat3_sub || "after AI optimization", color: "text-[#a855f7]" },
            { value: "5–10", label: t.sp_stat4 || "New Clients/Month", sub: t.sp_stat4_sub || "from organic traffic only", color: "text-blue-400" },
          ].map((stat, i) => (
            <div key={i} className="text-center p-6 bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-[var(--color-primary)] transition-colors" data-testid={`stat-${i}`}>
              <p className={`text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] ${stat.color}`}>{stat.value}</p>
              <p className="text-white font-bold text-sm mt-2">{stat.label}</p>
              <p className="text-[var(--color-text-muted)] text-xs mt-1 font-['JetBrains_Mono']">{stat.sub}</p>
            </div>
          ))}
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[
            { before: t.sp_case1_before || "Page 5 on Google", after: t.sp_case1_after || "Page 1, Position 3", time: t.sp_case1_time || "6 weeks", who: t.sp_case1_who || "E-commerce Store, ES" },
            { before: t.sp_case2_before || "0 AI citations", after: t.sp_case2_after || "Cited by ChatGPT in 8/10 queries", time: t.sp_case2_time || "2 months", who: t.sp_case2_who || "SaaS Startup, DE" },
            { before: t.sp_case3_before || "DA 12, 50 visitors/month", after: t.sp_case3_after || "DA 24, 890 visitors/month", time: t.sp_case3_time || "90 days", who: t.sp_case3_who || "Consulting Agency, SK" },
          ].map((c, i) => (
            <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-5" data-testid={`case-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 text-center p-2 bg-red-900/20 border border-purple-900/30">
                  <p className="text-[10px] text-red-400 font-bold uppercase">{t.sp_before || "Before"}</p>
                  <p className="text-xs text-red-300 font-mono mt-1">{c.before}</p>
                </div>
                <span className="text-[var(--color-text-muted)] text-lg">→</span>
                <div className="flex-1 text-center p-2 bg-emerald-900/20 border border-emerald-900/30">
                  <p className="text-[10px] text-emerald-400 font-bold uppercase">{t.sp_after || "After"}</p>
                  <p className="text-xs text-emerald-300 font-mono mt-1">{c.after}</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)]">
                <span className="font-['JetBrains_Mono']">{c.time}</span>
                <span>{c.who}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ReviewsSection({ t }) {
  const reviews = [
    { name: "Martin K.", role: "E-commerce Owner", country: "Bratislava, SK", text: t.rev1_text || "After 2 weeks with TopRankPulse, our WooCommerce store went from invisible to cited by ChatGPT. Traffic increased 34%. The AI visibility tracking is something no other tool offers.", boost: "+721% traffic" },
    { name: "Sarah L.", role: "Marketing Director", country: "London, UK", text: t.rev2_text || "We switched from Semrush to TopRankPulse. At €49/mo vs €130/mo, we get AI visibility tracking that Semrush simply doesn't have. The Blog Agent generated 25 articles that boosted our domain authority from 18 to 31.", boost: "DA 18 → 31" },
    { name: "Thomas B.", role: "SEO Consultant", country: "Berlin, DE", text: t.rev3_text || "I manage 40+ client websites. The SEO Agent monitoring saves me 10 hours/week. Automatic alerts when scores drop, PDF reports for clients — it's like having an extra team member for €49/mo.", boost: "10h/week saved" },
    { name: "Elena R.", role: "Startup Founder", country: "Barcelona, ES", text: t.rev4_text || "3,800 impressions per day in our hyper-niche industry. We went from a couple hundred to dominating AI search results. The competitor comparison showed us exactly what to fix.", boost: "3,800 imp/day" },
    { name: "Yuki T.", role: "Agency Owner", country: "Tokyo, JP", text: t.rev5_text || "26-language support is a game changer. We scan sites in Japanese, German, French — all from one dashboard. Won 3 new accounts by showing clients their AI visibility gaps vs competitors.", boost: "3 new clients" },
    { name: "Anna P.", role: "Content Manager", country: "Warszawa, PL", text: t.rev6_text || "The Blog Agent is incredible — 25 SEO articles for €25. Our domain authority jumped from 12 to 24 in 2 months. The WordPress plugin auto-publishes and the AI generates 2 images per article.", boost: "+47% CTR" },
  ];
  return (
    <section className="py-24 px-6 bg-[var(--color-surface)] border-t border-[var(--color-border)]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-[#FFE600] font-['JetBrains_Mono'] text-sm tracking-[0.3em] uppercase mb-4">{t.trusted_label}</p>
          <h2 className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4">
            {t.clients_title} <span className="text-[#a855f7]">{t.clients_highlight}</span> {t.clients_title2}
          </h2>
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="flex">{[1,2,3,4,5].map(i => <span key={i} className="text-[#FFE600] text-xl">&#9733;</span>)}</div>
            <span className="text-[var(--color-text-muted)] font-['JetBrains_Mono'] text-sm">{t.from_reviews}</span>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <div key={i} className="bg-[var(--color-bg)] border border-[var(--color-border)] p-6 hover:border-[var(--color-primary)] transition-colors" data-testid={`review-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex">{[1,2,3,4,5].map(s => <span key={s} className="text-[#FFE600] text-sm">&#9733;</span>)}</div>
                <span className="text-xs font-bold px-2 py-1 bg-emerald-900/30 text-emerald-400 font-['JetBrains_Mono']">{r.boost}</span>
              </div>
              <p className="text-[var(--color-text)] text-sm leading-relaxed mb-4">"{r.text}"</p>
              <div className="flex items-center gap-3 pt-4 border-t border-[var(--color-border)]">
                <div className="w-10 h-10 bg-[#a855f7]/20 flex items-center justify-center text-[#a855f7] font-black text-sm font-['Cabinet_Grotesk']">
                  {r.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <p className="text-white text-sm font-bold font-['Cabinet_Grotesk']">{r.name}</p>
                  <p className="text-[var(--color-text-muted)] text-xs">{r.role} — {r.country}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PricingSection({ t, onCheckout }) {
  const addons = [
    { id: "addon_brand_monitor", name: t.ao_bm_name || "Brand Monitor", price: "€19", period: true, href: "/brand-monitor" },
    { id: "addon_competitor_spy", name: t.ao_cs_name || "Competitor Spy", price: "€29", period: true, href: "/products/competitor-spy" },
    { id: "addon_attribution", name: t.ao_at_name || "AI Attribution", price: "€39", period: true, href: "/attribution" },
    { id: "addon_autopilot", name: t.ao_ap_name || "SEO Autopilot", price: "€79", period: true, href: "/products/seo-autopilot" },
    { id: "addon_ads_creator", name: t.ao_ac_name || "Ads Creator", price: "€69", period: true, href: "/ads-creator" },
    { id: "addon_blog_agent", name: t.ao_ba_name || "Blog Agent", price: "€25", period: false, href: "/products/blog-agent" },
    { id: "addon_fix_seo", name: t.ao_fx_name || "Fix My SEO", price: "€9", period: false, href: "/fix-my-seo" },
  ];
  return (
    <section className="py-24 px-6" id="pricing" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#a855f7]/10 text-[#a855f7] font-['JetBrains_Mono'] text-sm tracking-[0.2em] uppercase mb-4">{t.limited_time_offer}</span>
          <h2 className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4">{t.off_everything}</h2>
          <p className="text-[var(--color-text-muted)] text-lg">{t.lock_in}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {/* PRO */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-8 hover:border-[var(--color-primary)] transition-all" data-testid="plan-pro">
            <p className="text-[var(--color-text-muted)] font-['JetBrains_Mono'] text-sm uppercase tracking-wider mb-1">Pro</p>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-5xl font-black font-['Cabinet_Grotesk']">€49</span>
              <span className="text-[var(--color-text-muted)] text-sm">{t.per_month}</span>
            </div>
            <p className="text-[var(--color-text-muted)] text-sm line-through mb-6">€196{t.per_month}</p>
            <div className="inline-block px-3 py-1 bg-[#a855f7] text-white text-xs font-bold uppercase mb-6">{t.discount_badge}</div>
            <ul className="space-y-3 mb-8">
              {[t.pf1, t.pf2, t.pf3, t.pf4, t.pf5, t.pf6, t.pf7, t.pf8].map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-[var(--color-text)]"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />{f}</li>
              ))}
            </ul>
            <button onClick={() => onCheckout('pro_monthly')} className="block w-full py-3 bg-white text-black text-center font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors" data-testid="plan-pro-cta">{t.get_started}</button>
          </div>
          {/* BUSINESS */}
          <div className="bg-[var(--color-surface-alt)] border-2 border-[#FFE600] p-8 relative scale-[1.02]" data-testid="plan-business">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#FFE600] text-black text-xs font-black uppercase tracking-wider">{t.most_popular}</div>
            <p className="text-[#FFE600] font-['JetBrains_Mono'] text-sm uppercase tracking-wider mb-1">Business</p>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-5xl font-black font-['Cabinet_Grotesk']">€169</span>
              <span className="text-[var(--color-text-muted)] text-sm">{t.per_month}</span>
            </div>
            <p className="text-[var(--color-text-muted)] text-sm line-through mb-6">€676{t.per_month}</p>
            <div className="inline-block px-3 py-1 bg-[#a855f7] text-white text-xs font-bold uppercase mb-6">{t.discount_badge}</div>
            <ul className="space-y-3 mb-8">
              {[t.bf1, t.bf2, t.bf3, t.bf4, t.bf5, t.bf6, t.bf7, t.bf8].map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-[var(--color-text)]"><CheckCircle className="w-4 h-4 text-[#FFE600] flex-shrink-0" />{f}</li>
              ))}
            </ul>
            <button onClick={() => onCheckout('business_monthly')} className="block w-full py-3 bg-[#FFE600] text-black text-center font-black uppercase tracking-wider hover:bg-yellow-300 transition-colors" data-testid="plan-business-cta">{t.get_started}</button>
          </div>
          {/* ENTERPRISE */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-8 hover:border-[var(--color-primary)] transition-all" data-testid="plan-enterprise">
            <p className="text-[var(--color-text-muted)] font-['JetBrains_Mono'] text-sm uppercase tracking-wider mb-1">Enterprise</p>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-5xl font-black font-['Cabinet_Grotesk']">€399</span>
              <span className="text-[var(--color-text-muted)] text-sm">{t.per_month}</span>
            </div>
            <p className="text-[var(--color-text-muted)] text-sm line-through mb-6">€1,596{t.per_month}</p>
            <div className="inline-block px-3 py-1 bg-[#a855f7] text-white text-xs font-bold uppercase mb-6">{t.discount_badge}</div>
            <ul className="space-y-3 mb-8">
              {[t.ef1, t.ef2, t.ef3, t.ef4, t.ef5, t.ef6, t.ef7, t.ef8].map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-[var(--color-text)]"><CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />{f}</li>
              ))}
            </ul>
            <button onClick={() => onCheckout('enterprise_monthly')} className="block w-full py-3 bg-white text-black text-center font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors" data-testid="plan-enterprise-cta">{t.contact_sales}</button>
          </div>
        </div>
        <div className="mt-16 max-w-5xl mx-auto">
          <h3 className="text-2xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-center mb-8">{t.addons_title || "Add-ons"}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {addons.map((a, i) => (
              <a
                key={i}
                href={a.href}
                className="bg-[var(--color-surface)] border border-[var(--color-border)] p-4 text-center hover:border-[#a855f7] transition-all group block"
                data-testid={`addon-buy-${a.id}`}
              >
                <p className="text-white font-bold text-sm group-hover:text-[#a855f7] transition-colors">{a.name}</p>
                <p className="text-2xl font-black font-['Cabinet_Grotesk'] text-[#a855f7] mt-1">{a.price}<span className="text-xs text-[var(--color-text-muted)]">/{a.period ? (t.per_month?.replace("/", "") || "mo") : (t.ao_onetime || "one-time")}</span></p>
                <span className="inline-block mt-2 text-[10px] font-['JetBrains_Mono'] text-[var(--color-text-muted)] group-hover:text-[#a855f7] uppercase tracking-wider">{t.addon_learn_more || "Learn more →"}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
